import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '../stores/auth-store'
import { useOUStore } from '../stores/ou-store'

import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'

// Route meta type for permissions
declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    module?: string
    permission?: string
    public?: boolean
  }
}

const routes: Array<RouteRecordRaw> = [
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'my-dashboard' },
  },
  {
    name: 'admin',
    path: '/',
    component: AppLayout,
    redirect: { name: 'my-dashboard' },
    meta: { requiresAuth: true },
    children: [
      {
        name: 'my-dashboard',
        path: 'dashboard',
        component: () => import('../pages/dashboard/MyDashboardPage.vue'),
        meta: { public: true },
      },
      {
        name: 'time-in-out',
        path: 'time-in-out',
        component: () => import('../pages/time-in-out/TimeInOutPage.vue'),
        meta: { public: true },
      },
      {
        name: 'users',
        path: 'users',
        component: () => import('../pages/users/UsersPage.vue'),
        meta: { module: 'users', permission: 'view' },
      },
      {
        name: 'attendance',
        path: 'attendance',
        component: () => import('../pages/attendance/AttendancePage.vue'),
        meta: { module: 'attendance', permission: 'view' },
      },
      {
        name: 'shifts',
        path: 'shifts',
        component: () => import('../pages/shifts/ShiftsPage.vue'),
        meta: { module: 'shifts', permission: 'view' },
      },
      {
        name: 'work-schedules',
        path: 'work-schedules',
        component: () => import('../pages/work-schedules/WorkSchedulesPage.vue'),
        meta: { module: 'work-schedules', permission: 'view' },
      },
      {
        name: 'overtime',
        path: 'overtime',
        component: () => import('../pages/overtime/OvertimePage.vue'),
        meta: { module: 'overtime', permission: 'view' },
      },
      {
        name: 'official-business',
        path: 'official-business',
        component: () => import('../pages/official-business/OfficialBusinessPage.vue'),
        meta: { module: 'official-business', permission: 'view' },
      },
      {
        name: 'offset',
        path: 'offset',
        component: () => import('../pages/offset/OffsetPage.vue'),
        meta: { module: 'offset', permission: 'view' },
      },
      {
        name: 'leave',
        path: 'leave',
        component: () => import('../pages/leave/LeavePage.vue'),
        meta: { module: 'leave', permission: 'view' },
      },
      {
        name: 'holiday-calendar',
        path: 'holiday-calendar',
        component: () => import('../pages/leave/HolidayCalendarPage.vue'),
        meta: { module: 'leave', permission: 'view' },
      },
      {
        name: 'employee-leave-profile',
        path: 'employee-leave-profile',
        component: () => import('../pages/leave/EmployeeLeaveProfilePage.vue'),
        meta: { module: 'leave', permission: 'manage' },
      },
      {
        name: 'payroll',
        path: 'payroll',
        component: () => import('../pages/payroll/PayrollPage.vue'),
        meta: { module: 'payroll', permission: 'view' },
      },
      {
        name: 'payroll-settings',
        path: 'payroll/settings',
        component: () => import('../pages/payroll/PayrollSettingsPage.vue'),
        meta: { module: 'payroll', permission: 'manage' },
      },
      {
        name: 'reports',
        path: 'reports',
        component: () => import('../pages/reports/ReportsPage.vue'),
        meta: { module: 'reports', permission: 'view' },
      },
      {
        name: 'performance',
        path: 'performance',
        component: () => import('../pages/performance/PerformancePage.vue'),
        meta: { module: 'performance', permission: 'view' },
      },
      {
        name: 'training',
        path: 'training',
        component: () => import('../pages/training/TrainingPage.vue'),
        meta: { module: 'training', permission: 'view' },
      },
      {
        name: 'roles',
        path: 'roles',
        component: () => import('../pages/roles/RolesPage.vue'),
        meta: { module: 'roles', permission: 'view' },
      },
      {
        name: 'menus',
        path: 'menus',
        component: () => import('../pages/menus/MenusPage.vue'),
        meta: { module: 'menus', permission: 'view' },
      },
      {
        name: 'organizational-units',
        path: 'organizational-units',
        component: () => import('../pages/organizational-units/OrganizationalUnitsPage.vue'),
        meta: { module: 'organizational-units', permission: 'view' },
      },
      {
        name: 'modules',
        path: 'modules',
        component: () => import('../pages/modules/ModulesPage.vue'),
        meta: { module: 'modules', permission: 'view' },
      },
      {
        name: 'permissions',
        path: 'permissions',
        component: () => import('../pages/permissions/PermissionsPage.vue'),
        meta: { module: 'permissions', permission: 'view' },
      },
      {
        name: 'settings',
        path: 'settings',
        component: () => import('../pages/settings/Settings.vue'),
        meta: { public: true }, // Available to all authenticated users
      },
      {
        name: 'ag-grid-demo',
        path: 'ag-grid-demo',
        component: () => import('../pages/ag-grid-demo/AgGridDemoPage.vue'),
        meta: { public: true },
      },
      {
        name: 'employees',
        path: 'employees',
        component: () => import('../pages/employees/EmployeesPage.vue'),
        meta: { module: 'employees', permission: 'view' },
      },
      {
        name: 'employee-create',
        path: 'employees/create',
        component: () => import('../pages/employees/EmployeeFormPage.vue'),
        meta: { module: 'employees', permission: 'create' },
      },
      {
        name: 'org-chart',
        path: 'org-chart',
        component: () => import('../pages/org-chart/OrgChartPage.vue'),
        meta: { public: true }, // Available to all authenticated users
      },
      {
        name: 'employee-edit',
        path: 'employees/:id/edit',
        component: () => import('../pages/employees/EmployeeFormPage.vue'),
        meta: { module: 'employees', permission: 'update' },
      },
      {
        name: 'employee-profile',
        path: 'employees/:id',
        component: () => import('../pages/employees/EmployeeProfilePage.vue'),
        meta: { module: 'employees', permission: 'view' },
      },
      // ========================================
      // Memos Routes
      // ========================================
      {
        name: 'my-memos',
        path: 'memos/my-memos',
        component: () => import('../pages/Memos/MyMemos.vue'),
        meta: { public: true },
      },
      {
        name: 'memos-management',
        path: 'memos/management',
        component: () => import('../pages/Memos/MemosManagement.vue'),
        meta: { module: 'memos', permission: 'view' },
      },
      // ========================================
      // Audit Logs (Settings Module)
      // ========================================
      {
        name: 'audit-logs',
        path: 'settings/audit-logs',
        component: () => import('../pages/settings/AuditLogPage.vue'),
        meta: { module: 'settings', permission: 'view' },
      },
      // ========================================
      // Documentation Routes
      // ========================================
      {
        name: 'documentation',
        path: 'documentation',
        component: () => import('../pages/documentation/DocumentationPage.vue'),
        meta: { module: 'documentation', permission: 'view' },
      },
      {
        name: 'documentation-admin',
        path: 'documentation/admin',
        component: () => import('../pages/documentation/DocumentationAdminPage.vue'),
        meta: { module: 'documentation', permission: 'manage' },
      },
    ],
  },
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
      },
      {
        name: 'recover-password',
        path: 'recover-password',
        component: () => import('../pages/auth/RecoverPassword.vue'),
      },
      {
        name: 'recover-password-email',
        path: 'recover-password-email',
        component: () => import('../pages/auth/CheckTheEmail.vue'),
      },
      {
        path: '',
        redirect: { name: 'login' },
      },
    ],
  },
  {
    name: '403',
    path: '/403',
    component: () => import('../pages/403.vue'),
    meta: { public: true },
  },
  {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

// Navigation guard for authentication and permissions
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  const isAuthRoute = to.path.startsWith('/auth')
  const isAuthenticated = authStore.isLoggedIn

  // If user is authenticated and trying to access auth pages, redirect to dashboard
  if (isAuthenticated && isAuthRoute) {
    next({ name: 'my-dashboard' })
    return
  }

  // If user is not authenticated and trying to access protected pages, redirect to login
  if (!isAuthenticated && !isAuthRoute && to.name !== '404' && to.name !== '403') {
    next({ name: 'login' })
    return
  }

  // Check route-level permissions for authenticated users
  if (isAuthenticated && to.meta.module && to.meta.permission) {
    const ouStore = useOUStore()

    // Wait for OU store to initialize if not already
    if (!ouStore.initialized) {
      await ouStore.initialize()
    }

    // Permission codes should be lowercase (e.g., 'view', 'create')
    const hasPermission = ouStore.hasPermission(to.meta.module, to.meta.permission)

    if (!hasPermission) {
      next({
        name: '403',
        query: {
          module: to.meta.module,
          permission: to.meta.permission,
          from: to.fullPath,
        },
      })
      return
    }
  }

  // Allow navigation
  next()
})

export default router
